import { useEffect, useMemo, useRef, useState } from "preact/hooks";
import { track } from "../host/telemetry.js";
import { adNetworkForMe, houseAd } from "./ads.js";
import type { AdNetwork } from "./network.js";
import { appBanner, fillAdSense, inApp, loadAdSense } from "./network.js";

/** A phone held sideways: a 50 px strip would take an eighth of the height (ADR 0026). */
const SIDEWAYS = "(orientation: landscape) and (max-height: 500px)";

function useMedia(query: string): boolean {
  const [hit, setHit] = useState(() => matchMedia(query).matches);
  useEffect(() => {
    const m = matchMedia(query);
    const on = () => {
      setHit(m.matches);
    };
    m.addEventListener("change", on);
    return () => {
      m.removeEventListener("change", on);
    };
  }, [query]);
  return hit;
}

/**
 * The banner (ADR 0026): at the top, never near the controls or over the game. During play it
 * hides on a phone held sideways and comes back between matches. Its link opens a new tab while a
 * game is on, so it never ends one.
 */
export function AdBanner(props: BannerProps) {
  const sideways = useMedia(SIDEWAYS);
  const [fallback, setFallback] = useState(false);
  if (props.playing === true && sideways) return null;
  const net = adNetworkForMe.value;
  const toHouse = () => {
    setFallback(true);
  };
  if (net && !fallback) {
    // In the app, AdMob's banner during play; its menus keep Obean's own (a native banner stays
    // put while a page scrolls under it).
    if (inApp()) {
      if (props.playing && net.admob?.banner)
        return <AppBanner {...props} net={net} unit={net.admob.banner} onFail={toHouse} />;
    } else if (net.adsense?.bannerSlot)
      return (
        <WebBanner
          {...props}
          net={net}
          client={net.adsense.client}
          slot={net.adsense.bannerSlot}
          onFail={toHouse}
        />
      );
  }
  return <HouseBanner {...props} />;
}

interface BannerProps {
  where: "solo" | "room";
  playing?: boolean;
  gameId?: string | undefined;
}

const shown = (p: BannerProps, source: "house" | "network", adId: string) => {
  track("ad_shown", {
    slot: "banner",
    where: p.where,
    source,
    ad_id: adId,
    ...(p.gameId ? { game_id: p.gameId } : {}),
  });
};

/** AdMob's banner, drawn natively over this strip at the top (the app only). */
function AppBanner(p: BannerProps & { net: AdNetwork; unit: string; onFail: () => void }) {
  useEffect(() => {
    let remove: (() => void) | null = null;
    let gone = false;
    appBanner(p.net, p.unit).then((r) => {
      if (gone) r();
      else {
        remove = r;
        shown(p, "network", "network");
      }
    }, p.onFail);
    return () => {
      gone = true;
      remove?.();
    };
  }, [p.unit]);
  return <aside class="ad-banner native" aria-label="Advertisement" />;
}

/** An AdSense display unit in the strip; a house ad if it doesn't fill (the web only). */
function WebBanner(
  p: BannerProps & { net: AdNetwork; client: string; slot: string; onFail: () => void },
) {
  const unit = useRef<HTMLModElement>(null);
  useEffect(() => {
    const ins = unit.current;
    if (!ins) return;
    loadAdSense(p.net);
    void fillAdSense(ins);
    const watch = new MutationObserver(() => {
      const status = ins.dataset["adStatus"];
      if (status === "unfilled") p.onFail();
      if (status === "filled") shown(p, "network", "network");
    });
    watch.observe(ins, { attributes: true, attributeFilter: ["data-ad-status"] });
    return () => {
      watch.disconnect();
    };
  }, [p.slot]);
  return (
    <aside class="ad-banner network" aria-label="Advertisement">
      <span class="ad-tag">Ad</span>
      <ins
        ref={unit}
        class="adsbygoogle"
        style={{ display: "inline-block", width: "320px", height: "50px" }}
        data-ad-client={p.client}
        data-ad-slot={p.slot}
        {...(p.net.test ? { "data-adtest": "on" } : {})}
      />
    </aside>
  );
}

/** One of Obean's own ads: the Pass, a game, Beanwords. */
function HouseBanner(props: BannerProps) {
  const ad = useMemo(() => houseAd(Math.random(), props.gameId), [props.gameId]);
  useEffect(() => {
    track("ad_shown", {
      slot: "banner",
      where: props.where,
      source: "house",
      ad_id: ad.id,
      ...(props.gameId ? { game_id: props.gameId } : {}),
    });
  }, [ad.id]);
  return (
    <aside class="ad-banner" aria-label="Advertisement">
      <span class="ad-tag">Ad</span>
      <span class="ad-text">{ad.text}</span>
      <a
        href={ad.href}
        {...(props.playing ? { target: "_blank", rel: "noopener" } : {})}
        onClick={() => {
          track("ad_cta", { slot: "banner", where: props.where, ad_id: ad.id });
        }}
      >
        {ad.cta}
      </a>
    </aside>
  );
}

/** Whether the banner is showing during play, for the stage to make room under it. */
export function useBannerInPlay(on: boolean): boolean {
  const sideways = useMedia(SIDEWAYS);
  return on && !sideways;
}
